import type { GrowthCeilingKept } from '@messenger/utils'

export const PAGE_SURFACE_ATTR = 'data-page-surface'
export const PAGE_ID_ATTR = 'data-page-id'
export const PAGE_ZOOM_ATTR = 'data-page-zoom'

export const ITEM_RECT_ATTR = 'data-item-rect'
/** The item wrapper's selected state on the canvas (`Item.js`); absent on every other surface. */
export const ITEM_SELECTED_ATTR = 'data-item-selected'
export const TEXT_ITEM_ATTR = 'data-text-item'

export const EDITOR_TEXT_BOX_ATTR = 'data-editor-text-box'
export const EDITOR_TEXT_HOST_ATTR = 'data-editor-text-host'
export const EDITOR_TEXT_EDITING_ATTR = 'data-editor-text-editing'
export const EDITOR_TEXT_FIT_SETTLED_ATTR = 'data-editor-text-fit-settled'
/**
 * Stamped once, when a box first settles under loaded faces, and never
 * removed: the read-only paint stays hidden until then, so the first thing
 * a person sees is the fitted text in its own face, never a fallback.
 */
export const EDITOR_TEXT_REVEALED_ATTR = 'data-editor-text-revealed'
export const EDITOR_TEXT_FIT_VERSION_ATTR = 'data-editor-text-fit-version'
/**
 * On the pdf surface, the families whose face failed to load where they
 * paint a character of the box's text (`editorTextBoxFailedFamilies`). A box
 * carrying it has no settled stamp, and the PDF worker fails the job naming
 * them: a print never draws from a face that failed.
 */
export const EDITOR_TEXT_FACES_FAILED_ATTR = 'data-editor-text-faces-failed'

export const TEXT_LOADING_ATTR = 'data-text-loading'
/** The box's `itemState` as it is: `loading`, `idle` or `content-overflowing`. */
export const TEXT_ITEM_STATE_ATTR = 'data-text-item-state'
export const TEXT_FACES_PENDING_ATTR = 'data-text-faces-pending'
/**
 * What the settle before the load result concluded about the text's faces:
 * `loaded`, `error:<families>` (a face failed; the fallback is there for
 * good) or `undeclared:<families>` (no `@font-face` for a family the text
 * names, even once the document had loaded — a system font, or a
 * stylesheet that never came).
 */
export const TEXT_FACES_SETTLED_ATTR = 'data-text-faces-settled'
/**
 * Whether the content styles (`.mce-content-body`, line-height 1) had
 * reached the body and every text run before the load result: `ready`,
 * `ready-after:<frames>:<body|run>` (the settle waited that many frames;
 * which element was late), or `unstyled:<frames>:<body|run>` (they never
 * came and the result was taken anyway).
 */
export const TEXT_STYLES_SETTLED_ATTR = 'data-text-styles-settled'
/**
 * The tallest run's computed line-height and font-size, in px, and its
 * block's — the strut sets the line box too — at the instant the load
 * result read the body: `run=<lineHeight>/<fontSize>;block=<lineHeight>/<fontSize>`,
 * `normal` in place of a line-height that resolved `normal`, `block=none` for
 * a run in the body itself. A ratio other than the content rule's 1 on
 * either names a load result taken under a late style.
 */
export const TEXT_RUN_LINE_HEIGHT_ATTR = 'data-text-run-line-height'
/**
 * The body's blocks at the instant the load result read it:
 * `<children>:<bogus elements>:<head of the last child's markup>`, so an
 * extra line box that is gone by the time anyone looks — TinyMCE's caret
 * container, a bogus trailing paragraph, an empty line — is named.
 */
export const TEXT_BLOCKS_ATTR = 'data-text-blocks'
/**
 * The body's anatomy at that same instant, for the line box the blocks
 * stamp cannot name: `pad=<top>/<bottom>;nodes=<elements>/<all>;
 * stray=<root text nodes as code points>;blocks=<tag>=<height>/<lines>,…;
 * chips=<taking height>/<all>:<key>=<display>/<height>,…;inner=<head of the
 * last block's inner markup>`.
 */
export const TEXT_ANATOMY_ATTR = 'data-text-anatomy'
/**
 * How many load results the box's current text has painted: `0` until the
 * one result lands, `1` from then on, on every surface. The count resets
 * when the text is re-applied (a case value the box shows changed), so it
 * reads `1` again once that text's result has painted, never `2`; the
 * author's own resize is not a load result and does not count. A `2` means
 * one text was fitted twice.
 */
export const TEXT_FIT_PASSES_ATTR = 'data-text-fit-passes'
/**
 * How many times a face that landed after the load result refitted the
 * box's current text, counted apart from the load passes: such a refit
 * restarts the text's load, and its result does not move
 * TEXT_FIT_PASSES_ATTR. It resets with the pass count.
 */
export const TEXT_LATE_FACE_REFITS_ATTR = 'data-text-late-face-refits'
export const TEXT_WRITES_ATTR = 'data-text-writes'
/**
 * The frame the box painted on load: the row's stored height, on every
 * surface (a TinyMCE row with no stored height takes its text's height).
 */
export const TEXT_LOAD_HEIGHT_ATTR = 'data-text-load-height'
/**
 * What the load result did with the text inside the frame: `kept` (the text
 * stands inside the frame at its authored size), `grace:<overflow px>` (the
 * text ran past the frame by no more than the grace and keeps its size),
 * `fitted` (shrunk into the frame).
 */
export const TEXT_LOAD_FIT_ATTR = 'data-text-load-fit'
/**
 * The text sizing a Lotus box's read-only fit ran under, stamped beside the
 * load result on every surface: `shrink` (the text shrinks to fit the box
 * and is never scaled above its authored sizes) or `fill` (it scales up and
 * down to fill the box), and the maximum size in pt a Fill text box may
 * paint (`none` for no limit). A record of the fit's input, so a surface
 * that fitted under the wrong mode can be told apart from one that fitted
 * right.
 */
export const TEXT_SIZING_ATTR = 'data-text-sizing'
export const TEXT_MAX_SIZE_PT_ATTR = 'data-text-max-size-pt'
/** The room the page leaves under the box, in px: a record, applied to no frame. */
export const TEXT_CEILING_ATTR = 'data-text-ceiling'
/**
 * What bounds the room: a blocking item's id, `margin`, `fold`,
 * `preexisting` (the stored frame is past the room — a box saved over what
 * is below it; the three attributes below carry the room, what bounds it
 * and one line of the text), or `none` when only the page's bottom edge
 * bounds the box.
 */
export const TEXT_BOUNDED_BY_ATTR = 'data-text-bounded-by'
/**
 * Stamped beside `preexisting` alone: the room under the box, in px; what
 * bounds that room (an item id, `margin`, `fold`); and one line of the box's
 * own text in px (`none` when the surface measured no line). With the load
 * height, the stored frame, these are every number a refit into the room
 * would take.
 */
export const TEXT_KEPT_CEILING_ATTR = 'data-text-kept-ceiling'
export const TEXT_KEPT_BY_ATTR = 'data-text-kept-by'
export const TEXT_LINE_HEIGHT_ATTR = 'data-text-line-height'
/**
 * The box's own x, y and width as the room was measured from them,
 * comma-joined like `data-item-rect`. Two surfaces reading the same page
 * disagreed by a pixel and the number alone could not say which source each
 * had used, so the source travels with the number.
 */
export const TEXT_CEILING_FROM_ATTR = 'data-text-ceiling-from'
export const TEXT_BOUNDED_BY_NONE = 'none'
/** Worn by the autosize measurer's div for the instant it is in the DOM; never by a painted body. */
export const TEXT_MEASURE_ATTR = 'data-text-measure'
/**
 * The text's height at its authored sizes, before any fit, in px: on a
 * TinyMCE container the painted body's height, on a Lotus box root the
 * outer height the load fit measured with the frame at auto
 * (`EditorDriverFitResult.textHeightPx`). The height a frame would have to
 * hold the text at fit scale 1; a record, applied to no frame.
 */
export const TEXT_CONTENT_HEIGHT_ATTR = 'data-text-content-height'
/**
 * The measurer's reading of the same HTML at the same sizes: equal to the
 * content height within a pixel when the measurer lays out like the paint.
 */
export const TEXT_MEASURED_AT_ONE_ATTR = 'data-text-measured-at-one'

/** The three kept-room attributes for a result, or none when the frame is within its room. */
const keptCeilingAttributes = (
  kept: GrowthCeilingKept | null | undefined,
): Record<string, string> =>
  kept
    ? {
        [TEXT_KEPT_CEILING_ATTR]: String(kept.ceiling),
        [TEXT_KEPT_BY_ATTR]: kept.boundedBy ?? TEXT_BOUNDED_BY_NONE,
        [TEXT_LINE_HEIGHT_ATTR]:
          kept.lineHeight === null
            ? TEXT_BOUNDED_BY_NONE
            : String(kept.lineHeight),
      }
    : {}

const KEPT_CEILING_ATTRS = [
  TEXT_KEPT_CEILING_ATTR,
  TEXT_KEPT_BY_ATTR,
  TEXT_LINE_HEIGHT_ATTR,
] as const

/**
 * Writes the kept-room attributes for a result beside the load result, and
 * clears them when there are none, so a box whose later pass finds its frame
 * within its room carries no stale record.
 */
export const stampKeptCeiling = (
  el: Element,
  kept: GrowthCeilingKept | null | undefined,
): void => {
  const attrs = keptCeilingAttributes(kept)
  for (const name of KEPT_CEILING_ATTRS) {
    const value = attrs[name]
    if (value === undefined) el.removeAttribute(name)
    else el.setAttribute(name, value)
  }
}

export const itemRectAttribute = (
  x: unknown,
  y: unknown,
  width: unknown,
  height: unknown,
): string =>
  [x, y, width, height]
    .map((value) => {
      const n = typeof value === 'string' ? Number.parseFloat(value) : value
      return typeof n === 'number' && Number.isFinite(n) ? String(n) : ''
    })
    .join(',')

export const parseItemRectAttribute = (
  value: string | null,
): { x: number; y: number; width: number; height: number } | null => {
  if (!value) return null
  const [x, y, width, height] = value.split(',').map((part) => Number(part))
  if (![x, y, width, height].every((n) => Number.isFinite(n))) return null
  return { x, y, width, height }
}
